import { useTranslation } from "react-i18next";

export type ProcessOptionsType = (options: string[]) => {
  value: string;
  label: string;
}[];

const COLORS = [
  // Ardoise / Neutres
  { value: "#e5e7eb", name: "lightGray" },
  { value: "#64748b", name: "gray" },
  { value: "#0f172a", name: "darkGray" },

  // Bleu
  { value: "#93c5fd", name: "lightBlue" },
  { value: "#2563eb", name: "blue" },
  { value: "#1e40af", name: "darkBlue" },

  // Cyan
  { value: "#67e8f9", name: "lightCyan" },
  { value: "#0ea5e9", name: "cyan" },
  { value: "#075985", name: "darkCyan" },

  // Vert
  { value: "#6ee7b7", name: "lightGreen" },
  { value: "#10b981", name: "green" },
  { value: "#065f46", name: "darkGreen" },

  // Lime
  { value: "#bef264", name: "lightLime" },
  { value: "#84cc16", name: "lime" },
  { value: "#3f6212", name: "darkLime" },

  // Ambre
  { value: "#fde68a", name: "lightAmber" },
  { value: "#f59e0b", name: "amber" },
  { value: "#92400e", name: "darkAmber" },

  // Orange
  { value: "#fdba74", name: "lightOrange" },
  { value: "#f97316", name: "orange" },
  { value: "#9a3412", name: "darkOrange" },

  // Rouge
  { value: "#fca5a5", name: "lightRed" },
  { value: "#ef4444", name: "red" },
  { value: "#7f1d1d", name: "darkRed" },

  // Rose
  { value: "#fbcfe8", name: "lightPink" },
  { value: "#ec4899", name: "pink" },
  { value: "#9d174d", name: "darkPink" },

  // Violet
  { value: "#ddd6fe", name: "lightPurple" },
  { value: "#a855f7", name: "purple" },
  { value: "#5b21b6", name: "darkPurple" },
];

function ColorDot({
  color,
  name,
  selected,
  onClick,
}: {
  color: string;
  name: string;
  selected?: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative h-8 w-8 rounded-full transition
        hover:scale-110 focus:outline-none
        ${selected ? "ring-2 ring-offset-2 ring-slate-400" : ""}
      `}
      style={{ backgroundColor: color }}
    >
      {/* Tooltip */}
      <span
        className="
          pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2
          whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white
          opacity-0 transition group-hover:opacity-100
        "
      >
        {t(`components.colorSelect.${name}`)}
      </span>
    </button>
  );
}

type ColorPaletteProps = {
  value?: string | null;
  onChange?: (color: string | null) => void;
  className?: string;
};

function ColorSelect({ value, onChange, className }: ColorPaletteProps) {
  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {/* Option par défaut */}
      {COLORS.map((c) => (
        <div key={c.value} className="group">
          <ColorDot
            color={c.value}
            name={c.name}
            selected={value === c.value}
            onClick={() => onChange?.(c.value)}
          />
        </div>
      ))}
    </div>
  );
}

export default ColorSelect;
